'use client';

import { useRef, useEffect } from 'react';

export function GlobalBackground() {
    const containerRef = useRef<HTMLDivElement>(null);
    const spotlightRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current || !spotlightRef.current) return;

        let mouseCleanup: (() => void) | undefined;

        // Skip the cursor spotlight entirely on touch / coarse-pointer devices —
        // there's no mouse to track and the static halo sits over the hero text.
        const isCoarse =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(pointer: coarse)').matches;
        if (isCoarse) return;

        // GSAP is dynamic-imported so this layout-level component doesn't put
        // it in every page's entry chunk (TASK-4).
        let cancelled = false;
        let ctx: { revert: () => void } | undefined;

        (async () => {
        const { gsap } = await import('@/lib/gsap');
        if (cancelled || !containerRef.current) return;

        ctx = gsap.context(() => {
            // Set up spotlight tracking
            if (spotlightRef.current) {
                // Park the spotlight far off-screen until the user actually
                // moves the mouse — otherwise its halo sits over the hero on
                // first paint (visible in static SSR previews, screenshots,
                // and the brief moment before the first pointer event).
                gsap.set(spotlightRef.current, {
                    xPercent: -50,
                    yPercent: -50,
                    x: -2000,
                    y: -2000,
                    opacity: 0,
                });

                const spotX = gsap.quickTo(spotlightRef.current, "x", { duration: 0.6, ease: "power3.out" });
                const spotY = gsap.quickTo(spotlightRef.current, "y", { duration: 0.6, ease: "power3.out" });

                let revealed = false;
                const handleMouseMove = (e: MouseEvent) => {
                    if (!revealed) {
                        revealed = true;
                        gsap.to(spotlightRef.current, { opacity: 0.3, duration: 0.6, ease: 'power2.out' });
                    }
                    spotX(e.clientX);
                    spotY(e.clientY);
                };

                window.addEventListener("mousemove", handleMouseMove, { passive: true });
                mouseCleanup = () => window.removeEventListener("mousemove", handleMouseMove);
            }
        }, containerRef);
        })();

        return () => {
            cancelled = true;
            ctx?.revert();
            mouseCleanup?.();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[-2] pointer-events-none overflow-hidden"
            style={{ backgroundColor: '#173835' }}
        >
            {/* Interactive Spotlight Background */}
            <div
                ref={spotlightRef}
                className="absolute left-0 top-0 h-[600px] w-[600px] rounded-full opacity-0 blur-[100px] mix-blend-screen"
                style={{
                    background: 'radial-gradient(circle, rgba(0,255,200,1) 0%, rgba(0,255,200,0) 70%)',
                    willChange: 'transform'
                }}
            />
        </div>
    );
}
